import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../services/api';
import { useAuth } from '../../store/AuthContext';
import { Loader2, Zap, HelpCircle } from 'lucide-react';

const TurboModal = ({ isOpen, onClose, onImportComplete, initialConfig }) => {
  const { token } = useAuth();
  const [brandName, setBrandName] = useState('');
  const [targetCollection, setTargetCollection] = useState('base');
  const [country, setCountry] = useState('ES');
  const [type, setType] = useState('base');
  const [searchFields, setSearchFields] = useState({
    brand: true,
    name: true,
    amenity: false,
    shop: false
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (isOpen && initialConfig) {
      setType(initialConfig.type || 'base');
      setTargetCollection(initialConfig.collection || 'base');
    }
  }, [isOpen, initialConfig]);

  // Synchronize Type and Destination Collection
  useEffect(() => {
    if (type === 'competence') {
      setTargetCollection('competition');
    } else if (type === 'engine') {
      setTargetCollection('engine');
    } else {
      setTargetCollection('base');
    }
  }, [type]);

  if (!isOpen) return null;

  const handleImport = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const resp = await axios.post(`${getApiUrl()}/turbo/import`, {
        brandName,
        country,
        type,
        targetCollection,
        searchFields: Object.keys(searchFields).filter(k => searchFields[k])
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessage({ type: 'success', text: resp.data.message });
      if (onImportComplete) onImportComplete();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Import failed' });
    } finally {
      setLoading(false);
    }
  };

  const toggleSearchField = (field) => {
    setSearchFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(5, 7, 10, 0.9)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '16px',
      overflowY: 'auto',
      zIndex: 2000
    }}>
      <div style={{
        backgroundColor: '#1A1A1A',
        border: '1px solid #0B3D91',
        padding: '30px',
        width: '100%',
        maxWidth: '420px',
        maxHeight: 'calc(100dvh - 32px)',
        overflowY: 'auto',
        borderRadius: '4px',
        boxShadow: '0 0 30px rgba(0, 206, 201, 0.2)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <Zap color="#00CEC9" fill="#00CEC9" />
          <h2 style={{ color: '#F0F0F0', letterSpacing: '2px', fontSize: '1.2rem', margin: 0 }}>TURBO IMPORT</h2>
        </div>

        <form onSubmit={handleImport}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', color: '#888', fontSize: '0.7rem', marginBottom: '5px' }}>BRAND NAME / QUERY</label>
            <input
              className="nasa-input"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder="e.g. KFC, Lidl, BP"
              required
            />
          </div>

          <div style={{ marginBottom: '20px', border: '1px solid #333', padding: '12px', borderRadius: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <label style={{ color: '#00CEC9', fontSize: '0.65rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
                OSM SEARCH TARGETS
              </label>
              <a 
                href="/docs/intelligence/turbo-import" 
                target="_blank" 
                rel="noopener noreferrer"
                title="Help & Advanced Search Guide"
                style={{ color: '#555', transition: 'color 0.2s', display: 'flex', alignItems: 'center' }}
                onMouseOver={(e) => e.currentTarget.style.color = '#00CEC9'}
                onMouseOut={(e) => e.currentTarget.style.color = '#555'}
              >
                <HelpCircle size={14} />
              </a>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {Object.entries(searchFields).map(([key, checked]) => (
                <label key={key} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  color: checked ? '#FFF' : '#666',
                  transition: 'color 0.2s'
                }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSearchField(key)}
                    style={{ cursor: 'pointer', accentColor: '#00CEC9' }}
                  />
                  {key.toUpperCase()}
                </label>
              ))}
            </div>
            <p style={{ fontSize: '0.55rem', color: '#444', marginTop: '8px' }}>
              Restrict search to specific OpenStreetMap tags.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
            <div>
              <label style={{ display: 'block', color: '#888', fontSize: '0.7rem', marginBottom: '5px' }}>COUNTRY</label>
              <select 
                className="nasa-input" 
                value={country} 
                onChange={(e) => setCountry(e.target.value)}
              >
                <option value="ES">SPAIN (ES)</option>
                <option value="PT">PORTUGAL (PT)</option>
                <option value="FR">FRANCE (FR)</option>
                <option value="IT">ITALY (IT)</option>
                <option value="DE">GERMANY (DE)</option>
                <option value="UK">UNITED KINGDOM (UK)</option>
                <option value="AD">ANDORRA (AD)</option>
                <option value="GI">GIBRALTAR (GI)</option>
                <option value="BE">BELGIUM (BE)</option>
                <option value="NL">NETHERLANDS (NL)</option>
                <option value="CH">SWITZERLAND (CH)</option>
                <option value="AT">AUSTRIA (AT)</option>
                <option value="GR">GREECE (GR)</option>
                <option value="PL">POLAND (PL)</option>
                <option value="MA">MAROC (MA)</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', color: '#888', fontSize: '0.7rem', marginBottom: '5px' }}>CATEGORY</label>
              <select 
                className="nasa-input" 
                value={type} 
                onChange={(e) => setType(e.target.value)}
                disabled={!!initialConfig}
                style={{ opacity: initialConfig ? 0.6 : 1, cursor: initialConfig ? 'not-allowed' : 'pointer' }}
              >
                <option value="base">BASE</option>
                <option value="engine">ENGINE</option>
                <option value="competence">COMPETENCE</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', color: '#888', fontSize: '0.7rem', marginBottom: '5px' }}>DESTINATION COLLECTION</label>
            <select 
              className="nasa-input" 
              value={targetCollection} 
              disabled={true}
              style={{ opacity: 0.7, cursor: 'not-allowed', backgroundColor: 'rgba(255,255,255,0.02)' }}
            >
              <option value="base">BASE (MY SITES)</option>
              <option value="engine">ENGINE (ANALYSIS)</option>
              <option value="competition">COMPETITORS</option>
            </select>
            <p style={{ fontSize: '0.6rem', color: '#555', marginTop: '5px' }}>
              Automatically synced with mission category.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={onClose}
              className="nasa-button"
              style={{ flex: 1, backgroundColor: 'transparent' }}
            >
              ABORT
            </button>
            <button
              type="submit"
              disabled={loading}
              className="nasa-button"
              style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
              {loading ? 'IMPORTING...' : 'INITIATE TURBO'}
            </button>
          </div>
        </form>

        {message && (
          <div style={{
            marginTop: '20px',
            padding: '10px',
            fontSize: '0.8rem',
            backgroundColor: message.type === 'success' ? 'rgba(0, 184, 148, 0.1)' : 'rgba(214, 48, 49, 0.1)',
            color: message.type === 'success' ? '#00B894' : '#D63031',
            border: `1px solid ${message.type === 'success' ? '#00B894' : '#D63031'}`
          }}>
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
};

export default TurboModal;
